<template>
  <DefaultLayout :full-width="fullWidthLayout" @unit-added="() => redirectToList()">
    <DesktopDeviceHeader
      :tabs="tabs"
      :unit="unit"
      :selected="tab"
      @on-select="(t) => changeTab(t)"
      @go-to-list="() => $router.push('/')"
    >
      <template #default>
        <div v-if="unit">
          <h1>{{ unit.name || sn }}</h1>
        </div>
      </template>
      <template #subtitle>
        <div v-if="unit">
          <span v-if="unit.brandName">
            {{ unit.brandName?.primary }}
            <strong class="mr-0h">{{ unit.brandName?.secondary }}</strong>
          </span>
          <span class="ml-1">
            {{ unit.serialNumber }}
          </span>
          <span v-if="appLabelConfig?.poolLiveFront?.unitDetail?.showFWVersion && unit?.firmware" class="ml-1">
            v. {{ unit?.firmware }}
          </span>
        </div>
      </template>
    </DesktopDeviceHeader>
    <div>
      <template v-if="unit">
        <UnitDetailStatus v-if="tab === 'status'" :sn="sn" :unpaired="unpaired" />
        <EditUnitView v-if="tab === 'edit'" :sn="sn" @on-unpaired="handleUnpaired" />
        <UnitDetailHistoryChart v-if="tab === 'history'" :sn="sn" :unpaired="unpaired" :brand-name="unit?.brandName" />
        <UnitDetailConsumables v-if="tab === 'consumables' && !unit?.unitModel?.tabs?.hideConsumables" :sn="sn" :unpaired="unpaired" />
        <UnitDetailRequirements v-if="tab === 'requirements'" :sn="sn" :unpaired="unpaired" />
        <UnitDetailTimeline v-if="tab === 'timeline'" :sn="sn" :unpaired="unpaired" />
        <UnitDetailProtocols v-if="tab === 'protocols' && !unit?.unitModel?.tabs?.hideProtocolExport" :sn="sn" :unpaired="unpaired" />
      </template>
    </div>
  </DefaultLayout>
</template>

<script>
import UnitDetailStatus from './UnitDetailStatus.vue';
import UnitDetailConsumables from './UnitDetailConsumables.vue';
import UnitDetailRequirements from './UnitDetailRequirements.vue';
import UnitDetailTimeline from './UnitDetailTimeline.vue';
import UnitDetailHistoryChart from './UnitDetailHistoryChart.vue';

import { ViewQuery, removeUnitFromCache } from '@acs/components-library/src/gql/live/UnitDetail.js';
import { isDemo } from '@acs/components-library/src/demo.js';
import DefaultLayout from '../layouts/DefaultLayout.vue';
import DesktopDeviceHeader from '@acs/components-library/src/components/deviceDetail/website/DesktopDeviceHeader.vue';
import UnitDetailProtocols from './UnitDetailProtocols.vue';
import EditUnitView from './EditUnitView.vue';
import { appLabelConfig } from '@acs/components-library/src/appLabel/appLabelConfig.js';
import { useToastStore } from '../store/toastStore.js';

export default {
  components: {
    EditUnitView,
    UnitDetailProtocols,
    UnitDetailHistoryChart,
    DesktopDeviceHeader,
    DefaultLayout,
    UnitDetailStatus,
    UnitDetailConsumables,
    UnitDetailRequirements,
    UnitDetailTimeline,
  },
  setup() {
    const toastStore = useToastStore();
    return { toastStore };
  },
  apollo: {
    unitBySerialNumber: {
      query: ViewQuery,
      variables() {
        return {
          sn: this.sn,
        };
      },
      skip() {
        return this.unpaired || !this.sn;
      },
      result({ data }) {
        const forceBackToList = () => {
          this.unpaired = true; // avoids apollo cache to fill with errors
          // may trigger AFTER landing (transition) on List screen
          this.$router.replace({ name: 'UnitList' }).then(() => {
            this.tab = 'status'; // set default tab in case when unpaired when transitioning
            this.unpaired = false; // reset unpaired state
          });
        };
        switch (data?.unitBySerialNumber?.__typename) {
          case 'UnitNotFoundError':
            forceBackToList();
            this.toastStore.showError(this.$t('unit.unitNotFound'));
            removeUnitFromCache({ serialNumber: this.sn });
            break;
          case 'UnitAccessDeniedError':
            forceBackToList();
            this.toastStore.showError(this.$t('unit.unitAccessDenied'));
            removeUnitFromCache({ serialNumber: this.sn });
            break;
        }
      },
    },
  },
  data() {
    return {
      unpaired: false,
      tab: 'status',
      isDemo: isDemo(),
      appLabelConfig: appLabelConfig,
    };
  },
  computed: {
    sn() {
      return this.$route.params.sn;
    },
    unit() {
      return this.unitBySerialNumber;
    },
    unitNeverConnected() {
      return this.unit['__typename'] === 'UnitNeverConnected';
    },
    notificationAlerts() {
      return (this.unit?.notificationConfiguration || []).reduce(
        (acc, item) => (item.hasWarning ? acc + 1 : acc),
        0,
      );
    },
    consumableAlerts() {
      const consumables = this.unitBySerialNumber?.consumables || [];
      return consumables.reduce((acc, item) => {
        acc = item?.canister?.hasWarning ? acc + 1 : acc;
        acc = item?.tube?.hasWarning ? acc + 1 : acc;
        acc = item?.electrode?.hasWarning ? acc + 1 : acc;
        return acc;
      }, 0);
    },
    statusAlerts() {
      const statusMessages = this.unitBySerialNumber?.statusMessages || [];
      return statusMessages.reduce((acc, item) => {
        if (item.severity === 'ERROR') acc++;
        return acc;
      }, 0);
    },
    tabs() {
      if (!this.unit) return [];
      return [
        {
          name: this.$t('unit.tabs.status'),
          key: 'status',
          icon: 'aip-info-box',
          iconSize: '1.625rem',
          alert: this.statusAlerts,
          show: true,
        },
        {
          name: this.$t('unit.tabs.history'),
          key: 'history',
          icon: 'aip-graph',
          iconSize: '1.9375rem',
          alert: null,
          show: !this.unitNeverConnected,
        },
        {
          name: this.$t('unit.tabs.consumables'),
          key: 'consumables',
          icon: 'aip-canister',
          iconSize: '1.625rem',
          alert: this.consumableAlerts,
          show: !this.unit?.unitModel?.tabs?.hideConsumables && (this.unit?.consumables?.length !== 0 || !!this.unit?.heating || !!this.unit?.waterFilling) && !this.unitNeverConnected,
        },
        {
          name: this.$t('unit.tabs.requirements'),
          key: 'requirements',
          icon: 'aip-requirements',
          iconSize: '1.8rem',
          alert: null,
          show: !this.unitNeverConnected,
        },
        {
          name: this.$t('unit.tabs.timeline'),
          key: 'timeline',
          icon: 'aip-clock',
          iconSize: '1.625rem',
          alert: null,
          show: !this.unitNeverConnected,
        },
        {
          name: this.$t('unit.tabs.protocols'),
          key: 'protocols',
          icon: 'mdi-file',
          iconSize: '1.625rem',
          alert: null,
          show: !this.unit?.unitModel?.tabs?.hideProtocolExport && !this.unitNeverConnected && !isDemo(),
        },
        {
          name: this.$t('unit.tabs.edit'),
          key: 'edit',
          icon: 'aip-cog',
          iconSize: '1.1rem',
          alert: null,
          show: true,
        },
      ].filter((tab) => {
        return tab.show;
      }).map((tab) => {
        return {
          ...tab,
          icon: (this.appLabelConfig.poolLiveFront.unitDetail.showTabIcons) ? tab.icon : null,
        };
      });
    },
    fullWidthLayout() {
      return this.tab === 'history';
    },
  },
  watch: {
    unit() {
      const errors = [
        'UnitNotFoundError',
        'UnitAccessDeniedError',
      ];
      if (this.unit && this.unit.__typename && errors.includes(this.unit.__typename)) {
        console.error('Access denied', this.unit);
        this.$router.push({ name: 'UnitList' });
      }
    },
    '$route.params.tab': function (tab) {
      if (!tab) {
        this.tab = 'status';
        return;
      }
      this.tab = tab;
    },
  },
  created() {
    this.tab = this.$route.params.tab || 'status';
  },
  methods: {
    changeTab(tab) {
      this.$router.push({ name: 'UnitDetail', params: { sn: this.sn, tab } });
    },
    redirectToList() {
      this.$router.push({ name: 'UnitList' });
    },
    handleUnpaired() {
      this.unpaired = true;
      this.redirectToList();
      removeUnitFromCache({ serialNumber: this.sn });
    },
  },
};
</script>

<style lang="scss" scoped>
.content-header {
  margin: 0 .5rem 1rem;

  h2 {
    font-size: 1.5rem;
    line-height: 1.4;
    font-weight: 400;
    padding-bottom: 0;
  }

  p {
    font-size: 1.2rem;
    line-height: 1.4;
    font-weight: 300;
    padding-bottom: 0;
  }
}
</style>
